import React, { useState, useEffect } from 'react';
import { X, Plus, RotateCw, FileText, ArrowDownAZ, ArrowUpAZ, ChevronLeft, ChevronRight, GripVertical } from 'lucide-react';
import { API_BASE } from '../apiConfig';

const styles = {
  container: {
    padding: '20px 0',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '16px',
    marginBottom: '24px',
    position: 'relative',
  },
  stats: {
    display: 'inline-flex',
    alignItems: 'center',
    background: '#fff',
    border: '1px solid #e2e8f0',
    borderRadius: '12px',
    overflow: 'hidden',
    height: '36px',
    paddingRight: '16px',
    boxShadow: '0 4px 10px rgba(0,0,0,0.04)',
  },
  statsIcon: {
    background: '#4aba91',
    width: '44px',
    height: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#fff',
    position: 'relative',
    marginRight: '12px',
    clipPath: 'polygon(0% 0%, 85% 0%, 100% 50%, 85% 100%, 0% 100%)',
  },
  statsText: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    fontSize: '14px',
    color: '#1e293b',
    fontWeight: 700,
  },
  statsSize: {
    whiteSpace: 'nowrap',
  },
  statsSep: {
    width: '1px',
    height: '18px',
    background: '#e2e8f0',
  },
  statsCount: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    color: '#1e293b',
    fontWeight: 500,
  },
  statsCountNum: {
    color: '#4aba91',
    fontWeight: 700,
  },
  sortBtn: {
    width: '36px',
    height: '36px',
    borderRadius: '50%',
    background: '#fff',
    border: '1px solid var(--border)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    color: '#ef4444',
    boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
    transition: 'transform 0.1s, box-shadow 0.1s',
    position: 'relative',
  },
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))',
    gap: '20px',
    marginBottom: '24px',
  },
  card: {
    background: 'var(--surface)',
    border: '1px solid var(--border)',
    borderRadius: '12px',
    padding: '12px',
    position: 'relative',
    transition: 'transform 0.15s, border-color 0.15s',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    minHeight: '240px',
  },
  thumbnail: {
    width: '100%',
    aspectRatio: '1 / 1.41',
    background: 'var(--surface2)',
    borderRadius: '6px',
    marginBottom: '12px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'hidden',
    border: '1px solid var(--border)',
    transition: 'transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  },
  thumbImg: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
  },
  fileName: {
    fontSize: '12px',
    color: 'var(--text)',
    textAlign: 'center',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    width: '100%',
    fontWeight: 500,
  },
  actions: {
    position: 'absolute',
    top: '18px',
    right: '18px',
    display: 'flex',
    gap: '6px',
    zIndex: 10,
  },
  actionBtn: {
    width: '28px',
    height: '28px',
    borderRadius: '50%',
    background: 'var(--surface)',
    border: '1px solid var(--border-md)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    color: 'var(--text-muted)',
    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
    transition: 'background 0.2s, color 0.2s',
  },
  addCard: {
    background: 'rgba(59, 130, 246, 0.04)',
    border: '2px dashed var(--border-hi)',
    borderRadius: '12px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '12px',
    cursor: 'pointer',
    minHeight: '240px',
    transition: 'background 0.2s, border-color 0.2s',
  },
  addIcon: {
    width: '40px',
    height: '40px',
    borderRadius: '50%',
    background: 'var(--surface)',
    border: '1px solid var(--border-hi)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: 'var(--accent)',
  },
  tooltip: {
    position: 'absolute',
    top: '-50px',
    left: '50%',
    transform: 'translateX(-50%)',
    background: '#fff',
    border: '1px solid rgba(99, 102, 241, 0.15)',
    padding: '3px',
    paddingRight: '12px',
    borderRadius: '20px',
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    pointerEvents: 'none',
    boxShadow: '0 8px 20px rgba(99, 102, 241, 0.1)',
    zIndex: 100,
    animation: 'fadeUp 0.2s ease-out',
  },
  tooltipIcon: {
    width: '32px',
    height: '32px',
    borderRadius: '10px',
    background: '#6366f1',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#fff',
  },
  tooltipText: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    whiteSpace: 'nowrap',
  },
  tooltipSize: {
    fontSize: '14px',
    fontWeight: 800,
    color: '#1e293b',
  },
  tooltipSep: {
    width: '3px',
    height: '3px',
    borderRadius: '50%',
    background: '#818cf8',
  },
  tooltipPages: {
    fontSize: '14px',
    fontWeight: 600,
    color: '#475569',
  },
  sortTooltip: {
    position: 'absolute',
    right: '50px',
    top: '50%',
    transform: 'translateY(-50%)',
    background: '#334155',
    color: '#fff',
    padding: '6px 12px',
    borderRadius: '4px',
    fontSize: '12px',
    whiteSpace: 'nowrap',
    pointerEvents: 'none',
    boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
  },
  sortTooltipArrow: {
    position: 'absolute',
    right: '-4px',
    top: '50%',
    transform: 'translateY(-50%)',
    width: '0',
    height: '0',
    borderTop: '4px solid transparent',
    borderBottom: '4px solid transparent',
    borderLeft: '4px solid #334155',
  }
};

function formatSize(bytes) {
  if (bytes < 1024) return bytes + ' B';
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
  return (bytes / (1024 * 1024)).toFixed(2) + ' MB';
}

const Thumbnail = ({ file, rotation }) => {
  const [thumb, setThumb] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    const fetchThumb = async () => {
      try {
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 5000);
        
        const formData = new FormData();
        formData.append('file', file);
        formData.append('page', 0);
        
        const res = await fetch(`${API_BASE}/render-pdf`, {
          method: 'POST',
          body: formData,
          signal: controller.signal,
        });
        
        clearTimeout(timeoutId);
        
        if (res.ok && active) {
          const blob = await res.blob();
          setThumb(URL.createObjectURL(blob));
        }
      } catch (e) {
        console.error("Thumb failed", e);
      } finally {
        if (active) setLoading(false);
      }
    };

    fetchThumb();
    return () => { active = false; if (thumb) URL.revokeObjectURL(thumb); };
  }, [file]);

  return (
    <div style={{ ...styles.thumbnail, transform: `rotate(${rotation}deg)` }}>
      {loading ? (
        <RotateCw className="animate-spin" size={24} color="var(--text-muted)" />
      ) : thumb ? (
        <img src={thumb} alt="Preview" style={styles.thumbImg} />
      ) : (
        <FileText size={48} color="var(--border-hi)" />
      )}
    </div>
  );
};

const MergeCard = ({ file, rotation, onRemove, onRotate, onMove, isTouch, isFirst, isLast }) => {
  const [hovered, setHovered] = useState(false);
  const [pageCount, setPageCount] = useState(null);

  useEffect(() => {
    const fetchInfo = async () => {
      try {
        const formData = new FormData();
        formData.append('file', file);
        const res = await fetch(`${API_BASE}/convert/inspect`, {
          method: 'POST',
          body: formData,
        });
        if (res.ok) {
          const data = await res.json();
          setPageCount(data.totalPages);
        }
      } catch (e) {}
    };
    fetchInfo();
  }, [file]);

  return (
    <div 
      style={styles.card}
      onMouseEnter={() => !isTouch && setHovered(true)}
      onMouseLeave={() => !isTouch && setHovered(false)}
    >
      {(hovered || isTouch) && (
        <div style={{ ...styles.tooltip, top: isTouch ? 'auto' : '-50px', bottom: isTouch ? '-40px' : 'auto' }}>
          <div style={styles.tooltipIcon}>
            <FileText size={16} strokeWidth={2.5} />
          </div>
          <div style={styles.tooltipText}>
            <span style={styles.tooltipSize}>{formatSize(file.size)}</span>
            <div style={styles.tooltipSep} />
            <span style={styles.tooltipPages}>{pageCount !== null ? `${pageCount} pages` : '...'}</span>
          </div>
        </div>
      )}
      <div style={styles.actions}>
        <div 
          style={styles.actionBtn} 
          onClick={(e) => { e.stopPropagation(); onRotate(); }}
          title="Rotate 90°"
        >
          <RotateCw size={14} />
        </div>
        <div 
          style={styles.actionBtn} 
          onClick={(e) => { e.stopPropagation(); onRemove(); }}
          title="Remove"
        >
          <X size={16} />
        </div>
      </div>
      
      {isTouch && (
        <div style={{
            position: 'absolute',
            bottom: '50px',
            left: 0,
            right: 0,
            display: 'flex',
            justifyContent: 'space-between',
            padding: '0 8px',
            zIndex: 10
        }}>
            <div 
                style={{ ...styles.actionBtn, opacity: isFirst ? 0.3 : 1 }} 
                onClick={() => !isFirst && onMove(-1)}
            >
                <ChevronLeft size={16} />
            </div>
            <div 
                style={{ ...styles.actionBtn, opacity: isLast ? 0.3 : 1 }} 
                onClick={() => !isLast && onMove(1)}
            >
                <ChevronRight size={16} />
            </div>
        </div>
      )}

      <Thumbnail file={file} rotation={rotation} />
      <div style={styles.fileName}>{file.name}</div>
    </div>
  );
};

export default function PDFMerge({ files, rotations, onAddFiles, onRemoveFile, onRotateFile, onSortFiles, onReorderFiles, sortOrder }) {
  const inputRef = React.useRef(null);
  const [totalSize, setTotalSize] = useState(0);
  const [showSortTooltip, setShowSortTooltip] = useState(false);
  const [isTouch, setIsTouch] = useState(false);

  useEffect(() => {
    setIsTouch('ontouchstart' in window || navigator.maxTouchPoints > 0);
  }, []);

  useEffect(() => {
    setTotalSize(files.reduce((acc, f) => acc + f.file.size, 0));
  }, [files]);

  const handleSortClick = (e) => {
    e.currentTarget.style.transform = 'scale(0.95)';
    setTimeout(() => {
      if (e.currentTarget) e.currentTarget.style.transform = 'scale(1)';
    }, 100);
    onSortFiles();
  };

  return (
    <div style={styles.container}>
      <input 
        type="file" 
        multiple 
        accept=".pdf" 
        ref={inputRef} 
        style={{ display: 'none' }} 
        onChange={(e) => onAddFiles(Array.from(e.target.files))}
      />
      
      <div style={styles.header}>
        <div style={styles.stats}>
          <div style={styles.statsIcon}>
            <FileText size={18} strokeWidth={2.5} />
          </div>
          <div style={styles.statsText}>
            <span style={styles.statsSize}>{formatSize(totalSize)}</span>
            <div style={styles.statsSep} />
            <div style={styles.statsCount}>
              <span style={styles.statsCountNum}>{files.length}</span>
              <span>Files</span>
            </div>
          </div>
        </div>
        
        {files.length > 1 && (
          <div 
            style={styles.sortBtn} 
            onClick={handleSortClick}
            onMouseEnter={() => !isTouch && setShowSortTooltip(true)}
            onMouseLeave={() => !isTouch && setShowSortTooltip(false)}
          >
            {sortOrder === 'asc' ? <ArrowDownAZ size={18} /> : <ArrowUpAZ size={18} />}
            {showSortTooltip && (
              <div style={styles.sortTooltip}>
                {sortOrder === 'asc' ? 'Order files Z to A' : 'Order files A to Z'}
                <div style={styles.sortTooltipArrow} />
              </div>
            )}
          </div>
        )}
      </div>

      <div style={styles.grid}>
        {files.map((f, idx) => (
          <MergeCard
            key={f.id}
            file={f.file}
            rotation={rotations[idx] || 0}
            onRemove={() => onRemoveFile(idx)}
            onRotate={() => onRotateFile(idx)}
            onMove={(dir) => onReorderFiles(idx, idx + dir)}
            isTouch={isTouch}
            isFirst={idx === 0}
            isLast={idx === files.length - 1}
          />
        ))}
        
        <div style={styles.addCard} onClick={() => inputRef.current?.click()}>
          <div style={styles.addIcon}>
            <Plus size={24} />
          </div>
          <div style={{ fontSize: '13px', fontWeight: 600, color: 'var(--text-muted)' }}>
            Add more files
          </div>
        </div>
      </div>
    </div>
  );
}
